import React, { useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import moment from 'moment';
import { FaPlus, FaArchive, FaBoxOpen, FaWhatsapp, FaRegCommentDots } from 'react-icons/fa';

export interface ChatThread {
    id: number;
    user_id: number;
    title: string;
    is_archived: boolean;
    initiator: string | null;
    created_at: string;
    updated_at: string;
    last_user_id?: number | null;
    last_user_name?: string | null;  // resolved server-side via JOIN with users
    last_user_message?: string | null;
    last_message_mode?: string | null;
    last_message_time?: string | null;
}

interface RecentChatsProps {
    threads: ChatThread[];
    activeThreadId: number | null;
    onSelectThread: (threadId: number) => void;
    onNewChat: () => void;
    onArchiveThread: (threadId: number, archive: boolean) => void;
    showArchived: boolean;
    onToggleShowArchived: () => void;
}

const RecentChats: React.FC<RecentChatsProps> = ({
    threads,
    activeThreadId,
    onSelectThread,
    onNewChat,
    onArchiveThread,
    showArchived,
    onToggleShowArchived,
}) => {
    return (
        <aside className="flex flex-col max-h-full bg-white border-r border-slate-200 lg:w-72 shadow-md rounded-lg overflow-hidden shrink-0">
            {/* Header & New Chat Button */}
            <div className="p-4 border-b border-slate-200">
                <button
                    onClick={onNewChat}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer"
                >
                    <FaPlus className="text-xs" />
                    New Chat
                </button>
            </div>

            {/* List Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Recent
                </h3>
                <button
                    onClick={onToggleShowArchived}
                    className="text-xs text-slate-500 hover:text-blue-600 font-medium transition-colors"
                >
                    {showArchived ? 'Hide Archived' : 'Show Archived'}
                </button>
            </div>

            {/* Virtuoso Virtualized List */}
            <div className="flex-1 min-h-0 bg-slate-50">
                {threads.length === 0 ? (
                    <div className="p-4 text-center text-sm text-slate-500 mt-4">
                        No chats found.
                    </div>
                ) : (
                    <Virtuoso
                        data={threads}
                        className="h-full"
                        itemContent={(_index, thread) => {
                            const isActive = thread.id === activeThreadId;
                            
                            return (
                                <div
                                    key={thread.id}
                                    onClick={() => onSelectThread(thread.id)}
                                    className={`group flex items-start justify-between p-3 mx-2 my-1 cursor-pointer rounded-lg border transition-all ${
                                        isActive
                                            ? 'bg-white border-blue-200 shadow-sm'
                                            : 'border-transparent hover:bg-slate-100'
                                    }`}
                                >
                                    <div className="flex-1 min-w-0 pr-2">
                                        <h4
                                            className={`text-sm truncate font-medium ${
                                                isActive ? 'text-blue-700' : 'text-slate-700'
                                            }`}
                                        >
                                            <span className={`mr-1.5 font-mono text-xs ${isActive ? 'text-blue-400' : 'text-slate-400'}`}>#{thread.id}</span>
                                            {thread.title}
                                        </h4>
                                        <div className="text-xs text-slate-600 font-medium mt-1 truncate">
                                            {thread.last_user_name || 'User'}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                                            {thread.last_user_message ? (thread.last_user_message.length > 50 ? thread.last_user_message.substring(0, 50) + '...' : thread.last_user_message) : 'No message'}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-1 flex items-center">
                                            {thread.last_message_mode === 'whatsapp' ? (
                                                <FaWhatsapp className="mr-1 text-green-500" />
                                            ) : (
                                                <FaRegCommentDots className="mr-1 text-blue-500" />
                                            )}
                                            <span>
                                                {moment(thread.last_message_time || thread.updated_at).format('lll')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onArchiveThread(thread.id, !thread.is_archived);
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                            data-tooltip-id="my-tooltip"
                                            data-tooltip-content={thread.is_archived ? 'Unarchive' : 'Archive'}
                                        >
                                            {thread.is_archived ? (
                                                <FaBoxOpen className="text-xs" />
                                            ) : (
                                                <FaArchive className="text-xs" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        }}
                    />
                )}
            </div>
        </aside>
    );
};

export default RecentChats;
